import React, { useEffect, useState } from "react";
import { toast } from "sonner";

const inputCls = "mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-[#1C64B0] focus:outline-none";
const labelCls = "flex flex-col text-xs font-semibold text-slate-600";

export default function ReflexKoppelingPanel({ api, btn }) {
  const [cfg, setCfg] = useState(null);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [open, setOpen] = useState(false);
  const [advanced, setAdvanced] = useState(false);

  const load = async () => {
    try { const r = await api.get("/reflex/config"); setCfg(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Kon de koppeling-instellingen niet laden."); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!cfg) return null;

  const setField = (k, v) => setCfg((p) => ({ ...p, [k]: v }));
  const setLoc = (i, k, v) => setCfg((p) => ({ ...p, locaties: p.locaties.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        baseUrl: cfg.baseUrl || "", administration: cfg.administration || "1", apiVersion: cfg.apiVersion || 2,
        username: cfg.username || "", stockPath: cfg.stockPath, stockBody: cfg.stockBody, mapping: cfg.mapping,
        locaties: (cfg.locaties || []).map((l) => ({ label: l.label, warehouseId: l.warehouseId || "" })),
      };
      if (pw) payload.password = pw;
      const r = await api.put("/reflex/config", payload);
      setCfg(r.data); setPw("");
      toast.success("Koppeling opgeslagen.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt.");
    } finally { setBusy(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post("/reflex/test");
      toast.success(`Verbinding OK — ${r.data.aantalRegels} regel(s) opgehaald voor "${r.data.locatie || "magazijn"}".`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verbinding mislukt.");
    } finally { setTesting(false); }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="reflex-panel">
      <button type="button" data-testid="reflex-panel-toggle" onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-4 py-3 text-left">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          Live ReflexBlue-koppeling
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${cfg.passwordConfigured && cfg.baseUrl ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
            {cfg.passwordConfigured && cfg.baseUrl ? "ingesteld" : "nog niet ingesteld"}
          </span>
        </span>
        <span className="text-slate-400 text-sm">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="space-y-4 border-t border-slate-100 px-4 py-4">
          <p className="text-xs text-slate-500 max-w-3xl">
            De koppeling is <span className="font-semibold">alleen-lezen</span>: het programma haalt uitsluitend voorraad op bij ReflexBlue en stuurt er niets heen.
            Gebruik een apart API-account ("External seat") met alleen leesrechten — niet je persoonlijke login. De inloggegevens worden versleuteld op de server bewaard en nooit teruggetoond.
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className={labelCls}>Portal-URL
              <input data-testid="reflex-baseurl" className={inputCls} placeholder="https://klant.reflex-blue.cloud.reflex-systems.nl"
                value={cfg.baseUrl || ""} onChange={(e) => setField("baseUrl", e.target.value)} />
            </label>
            <label className={labelCls}>Administratienummer
              <input data-testid="reflex-admin" className={inputCls} placeholder="1"
                value={cfg.administration || ""} onChange={(e) => setField("administration", e.target.value)} />
            </label>
            <label className={labelCls}>Gebruikersnaam (API-account)
              <input data-testid="reflex-username" className={inputCls} autoComplete="off"
                value={cfg.username || ""} onChange={(e) => setField("username", e.target.value)} />
            </label>
            <label className={labelCls}>Wachtwoord
              <input data-testid="reflex-password" type="password" className={inputCls} autoComplete="new-password"
                placeholder={cfg.passwordConfigured ? "•••••••• (ingesteld — laat leeg om te behouden)" : "wachtwoord invullen"}
                value={pw} onChange={(e) => setPw(e.target.value)} />
            </label>
          </div>

          <div>
            <div className="text-xs font-semibold text-slate-600 mb-1">Magazijnen (vrieshuizen)</div>
            <div className="space-y-2">
              {(cfg.locaties || []).map((l, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2">
                  <input data-testid={`reflex-loc-label-${i}`} className="w-48 rounded-md border border-slate-300 px-3 py-1.5 text-sm"
                    value={l.label} onChange={(e) => setLoc(i, "label", e.target.value)} placeholder="Naam locatie" />
                  <span className="text-xs text-slate-400">magazijn-ID:</span>
                  <input data-testid={`reflex-loc-wh-${i}`} className="w-40 rounded-md border border-slate-300 px-3 py-1.5 text-sm"
                    value={l.warehouseId || ""} onChange={(e) => setLoc(i, "warehouseId", e.target.value)} placeholder="warehouse id" />
                </div>
              ))}
            </div>
          </div>

          <details className="rounded-md bg-slate-50 px-3 py-2" open={advanced} onToggle={(e) => setAdvanced(e.target.open)}>
            <summary className="cursor-pointer text-xs font-semibold text-slate-500">Geavanceerd (voorraad-pad, verzoek & veldkoppeling)</summary>
            <div className="mt-3 space-y-3">
              <label className={labelCls}>Voorraad-pad (POST)
                <input data-testid="reflex-stockpath" className={inputCls} value={cfg.stockPath || ""} onChange={(e) => setField("stockPath", e.target.value)} />
              </label>
              <label className={labelCls}>Verzoek-body (JSON)
                <textarea data-testid="reflex-stockbody" rows={3} className={`${inputCls} font-mono text-xs`}
                  value={JSON.stringify(cfg.stockBody || {}, null, 2)}
                  onChange={(e) => { try { setField("stockBody", JSON.parse(e.target.value)); } catch { /* laat ongeldig staan tot geldig */ } }} />
              </label>
              <label className={labelCls}>Veldkoppeling (JSON): artikelnr, naam, aantal, gewicht_kg, locatie
                <textarea data-testid="reflex-mapping" rows={4} className={`${inputCls} font-mono text-xs`}
                  value={JSON.stringify(cfg.mapping || {}, null, 2)}
                  onChange={(e) => { try { setField("mapping", JSON.parse(e.target.value)); } catch { /* idem */ } }} />
              </label>
              <p className="text-[11px] text-slate-400">Het exacte voorraad-pad en de veldnamen verschillen per ReflexBlue-omgeving. Vul deze samen met finance/ReflexSystems in aan de hand van de Swagger-documentatie.</p>
            </div>
          </details>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button data-testid="reflex-save" disabled={busy} onClick={save} className={btn}>{busy ? "Opslaan…" : "Opslaan"}</button>
            <button data-testid="reflex-test" disabled={testing} onClick={test} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              {testing ? "Testen…" : "Verbinding testen"}
            </button>
            {cfg.updatedAt && <span className="text-xs text-slate-400">laatst gewijzigd {new Date(cfg.updatedAt).toLocaleString("nl-NL")} · {cfg.updatedBy || "—"}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
